import React, { useCallback, memo } from 'react'
import {
  Table as BaseTable,
  TableHead,
  TableBody,
  TableFoot,
  TableRow,
  TableHeader,
  TableCell
} from '../ui/Table'
import { useTableSort } from './hooks'
import { COLUMN_HEADER_MAX_WIDTH, DISPLAY_MODES } from './constants'
import EmptyState from '../ui/EmptyState'
import VariableCell from './VariableCell'

const SelectionTable = ({ statistics, displayMode = DISPLAY_MODES.COUNT, filterUrl, variableId }) => {
  const getSortValue = useCallback((row, column) => {
    if (column === 'value')
      return (row.value || '').toLowerCase()
    if (column === 'total')
      return row.total
    if (column === 'noResponse')
      return row.noResponseCount || 0
    return row.counts[column] || 0
  }, [])

  const { sortedRows, sortProps } = useTableSort(statistics?.rows, getSortValue)

  const formatValue = useCallback((count, total) => {
    if (displayMode !== DISPLAY_MODES.COUNT && total > 0)
      return `${(count / total * 100).toFixed(1)}%`
    return count
  }, [displayMode])

  if (!statistics?.rows?.length)
    return <EmptyState />

  const { columns, totals, hasNoResponse } = statistics

  return (
    <div className="mt-3">
      <BaseTable hover>
        <TableHead>
          <TableRow>
            <TableHeader {...sortProps('value')} className="text-nowrap">
              Variable
            </TableHeader>
            {columns.map(column => (
              <TableHeader key={column.id} className="text-center" {...sortProps(column.id)}>
                <span
                  className="d-inline-block text-truncate align-middle"
                  style={COLUMN_HEADER_MAX_WIDTH}
                  title={column.name}
                >
                  {column.name}
                </span>
              </TableHeader>
            ))}
            {hasNoResponse ? (
              <TableHeader className="text-center" {...sortProps('noResponse')}>
                Sans réponse
              </TableHeader>
            ) : null}
            <TableHeader
              className="text-center text-nowrap bg-light"
              {...sortProps('total')}
            >
              Total
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => {
            const rowDenom = displayMode === DISPLAY_MODES.ROW_PERCENTAGE ? row.total : totals.grand
            return (
              <TableRow key={row.value || `empty-${index}`}>
                <VariableCell filterUrl={filterUrl} row={row} variableId={variableId} />
                {columns.map(column => (
                  <TableCell key={column.id} className="text-center">
                    {formatValue(row.counts[column.id] || 0, rowDenom)}
                  </TableCell>
                ))}
                {hasNoResponse ? (
                  <TableCell className="text-center">
                    {formatValue(row.noResponseCount || 0, rowDenom)}
                  </TableCell>
                ) : null}
                <TableCell className="text-center font-weight-bold bg-light-hover">
                  {formatValue(row.total, rowDenom)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
        <TableFoot>
          <TableRow className="bg-light">
            <th>Total</th>
            {columns.map(column => (
              <th key={column.id} className="text-center">
                {formatValue(totals.byColumn[column.id] || 0, totals.grand)}
              </th>
            ))}
            {hasNoResponse ? (
              <th className="text-center">
                {formatValue(totals.noResponse || 0, totals.grand)}
              </th>
            ) : null}
            <th className="text-center">
              {displayMode === DISPLAY_MODES.COUNT ? totals.grand : '100%'}
            </th>
          </TableRow>
        </TableFoot>
      </BaseTable>
    </div>
  )
}

export default memo(SelectionTable)
